/* eslint-disable no-param-reassign */
import { faCommentsAlt } from '@fortawesome/pro-duotone-svg-icons';
import { WebViewerInstance } from '@pdftron/webviewer';
import { ImageProps, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileAttachment } from 'api/attachments';
import { AttachmentType } from 'api/attachments/types';
import { ProjectId } from 'api/models';
import { Empty } from 'components/atoms/empty';
import {
  Container,
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { ConversationSkeleton } from 'components/organisms/conversations/loading/index';
import { MessagesList } from 'components/organisms/conversations/messages-list/index';
import { useConversationStore } from 'data/conversation';
import { AttachmentDiscussion } from './attachment-discussion';
import { WORKSPACE_ID } from './constants';
import { FloatingComponent } from './floating-component';
import { FloatingForm } from './floating-form';
import './image-preview.css';
import { PreviewToolbar } from './preview-toolbar';
import { useImagePreview } from './use-image-preview';

const { TabPane } = Tabs;

const StyledModal = styled.div`
  height: 100%;
  width: 100%;
  top: 0;
  overflow-y: hidden;
  max-height: 100vh;
  .ant-modal-content {
    background-color: transparent;
    width: 100%;
    box-shadow: none;
  }
  .ant-modal-body {
    padding: 0;
    width: 100%;
  }
  .ant-modal-footer {
    display: none;
  }
`;

const StyledDiv = styled.div`
  height: 100%;
  width: 100%;
  flex-grow: 1;
`;

const CommentsSection = styled.div<{
  isClose: boolean;
  isTaskAttachment: boolean;
}>`
  height: 100%;
  background: var(--coolest-gray-10);
  overflow-y: auto;
  scroll-behavior: smooth;
  padding: ${(props) =>
    !props.isClose && props.isTaskAttachment ? 'var(--spacing-04)' : '0'};
  width: ${(props) => (props.isClose ? '0' : '550px')};
  overflow-x: hidden;
  transition: all 0.5s cubic-bezier(0, 0, 0.3, 1);
  display: flex;
  flex-direction: column;
`;

const CommentAndPreviewWrapper = styled.div<{ isComparisonMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ isComparisonMode }) =>
    isComparisonMode ? 'calc(100vh - 104px)' : '100%'};
  width: 100%;
  background-color: var(--coolest-gray-10);
  overflow: hidden;
`;

const ToolbarAndPreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex-grow: 1;
`;

const StyledTabs = styled(Tabs)`
  &,
  .ant-tabs,
  .ant-tabs-content-holder,
  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-nav-list {
    width: 99%;
  }
  .ant-tabs-tab {
    flex: 1;
    overflow: hidden;
  }

  .ant-tabs-nav {
    padding: var(--spacing-04);
    padding-bottom: 0;
  }

  .ant-tabs-content-holder {
    overflow-y: auto;
    padding: var(--spacing-04);
  }
`;

const TabPaneStyled = styled(TabPane)`
  display: flex;
  flex-direction: column;
`;

const ListWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  overflow-y: auto;
`;

const CommentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-03);
`;

const CommentCount = styled.div`
  font-size: 14px;
  color: #000000;
`;

const HideResolvedText = styled.div`
  font-weight: var(--heading-4-weight);
  font-size: var(--spacing-04);
  color: var(--blue-60);
  cursor: pointer;
`;

const ShowResolvedText = styled.div`
  font-weight: var(--heading-4-weight);
  font-size: var(--spacing-04);
  margin-top: var(--spacing-05);
  color: var(--blue-60);
  cursor: pointer;
`;

const StyledEmpty = styled(Empty)`
  justify-content: center;
  height: max-content;
  flex-grow: 1;
`;

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    padding: var(--spacing-08) var(--spacing-06) var(--spacing-05);
  }
  ${Container} {
    width: auto;
    max-width: max-content;

    ${EmptyState.Title}, ${EmptyState.Message} {
      width: 100%;
    }
  }
`;

export type ImagePreviewProps = ImageProps & {
  attachmentId?: number | undefined;
  data: FileAttachment;
  isChatSectionVisible?: boolean;
  setChatSectionVisiblity?: (value: boolean) => void;
  isLeftAttachment?: boolean;
  isRightAttachment?: boolean;
  setSelectedInstance?: (instance: WebViewerInstance | null) => void;
  setId?: (idValue: number) => void;
  activeToolOverlay?: string;
  setActiveToolOverlay?: (activeTool: string) => void;
  setDiscussionAttachmentId?: (value: number) => void;
  showResolvedCommentsCompare?: boolean;
  setShowResolvedCommentsComapre?: (value: boolean) => void;
  previewConfig?: {
    zoomTogether?: boolean;
    rotateTogether?: boolean;
    isOverlay?: boolean;
    scrollTogether?: boolean;
    isSideBySide?: boolean;
  };
};

// TODO: rename the component @chaithanyaangalagunta
export const ImagePreview: React.FC<ImagePreviewProps> = ({
  attachmentId,
  data,
  isChatSectionVisible,
  setChatSectionVisiblity,
  isLeftAttachment,
  isRightAttachment,
  setSelectedInstance,
  setId,
  activeToolOverlay,
  setActiveToolOverlay,
  setDiscussionAttachmentId,
  showResolvedCommentsCompare,
  setShowResolvedCommentsComapre,
  previewConfig,
  ...props
}) => {
  const [showResolvedComments, setShowResolvedComments] = useState(false);
  const {
    instance,
    ref,
    addNewCommentFormVisiblity,
    floatingFormCoordinates,
    activeDiscussion,
    setAddNewCommentFormVisiblity,
    filteredDicsussions,
    activeAnnotation,
    onSubmit,
    isPrivate,
    onPrivateButtonClick,
    setActiveAnnotation,
    setActiveDiscussion,
    setFloatingFormCoordinates,
    activeTool,
    zoom,
    discussionsInAttachment,
    discussions,
    resolvedDiscussions,
  } = useImagePreview({
    data,
    attachmentId,
    src: props.src,
    activeToolOverlay,
    setSelectedInstance,
    setDiscussionAttachmentId,
    setActiveToolOverlay,
    isRightAttachment,
    setChatSectionVisiblity,
    previewConfig,
    showResolvedComments,
    setShowResolvedComments: (value: boolean) => setShowResolvedComments(value),
  });

  const { comments: controlComments, loading } = useConversationStore(
    ProjectId(data.projectId),
    {
      type: 'ATTACHMENTS',
      id: Number(data.versionMappingId),
    },
  );

  useEffect(() => {
    if (showResolvedCommentsCompare !== undefined) {
      setShowResolvedComments(showResolvedCommentsCompare);
    }
  }, [showResolvedCommentsCompare]);

  return (
    <>
      {data &&
      instance.current &&
      addNewCommentFormVisiblity &&
      floatingFormCoordinates.x > 0 &&
      floatingFormCoordinates.y > 0 &&
      !activeDiscussion ? (
        <FloatingForm
          onClose={() => {
            setAddNewCommentFormVisiblity(false);
            const _disucssion = filteredDicsussions.find((d) =>
              d.comments.find(
                (comment) => comment.commentGuid === activeAnnotation?.Id,
              ),
            );

            if (!_disucssion && activeAnnotation) {
              instance.current?.Core.annotationManager.deleteAnnotation(
                activeAnnotation,
              );
            }
          }}
          onSubmit={onSubmit}
          projectId={data?.projectId}
          y={floatingFormCoordinates.y}
          x={floatingFormCoordinates.x}
          isPrivate={isPrivate}
          onPrivateBtnClick={onPrivateButtonClick}
          isLeftAttachment={isLeftAttachment}
        />
      ) : null}
      {data &&
      instance.current &&
      attachmentId &&
      activeDiscussion &&
      floatingFormCoordinates.x > 0 &&
      floatingFormCoordinates.y > 0 ? (
        <FloatingComponent
          x={floatingFormCoordinates.x}
          y={floatingFormCoordinates.y}
          isLeftAttachment={isLeftAttachment}
        >
          <AttachmentDiscussion
            activeDiscussion={activeDiscussion}
            attachmentId={attachmentId}
            discussion={activeDiscussion}
            instance={instance.current}
            projectId={data.projectId}
            setActiveAnnotation={setActiveAnnotation}
            setActiveDiscussion={setActiveDiscussion}
            setFloatingFormCoordinates={setFloatingFormCoordinates}
            onDiscussionClick={() => setActiveDiscussion(activeDiscussion)}
            showResolvedComments={showResolvedComments}
          />
        </FloatingComponent>
      ) : null}
      <StyledModal>
        <CommentAndPreviewWrapper
          isComparisonMode={
            previewConfig?.isOverlay ?? previewConfig?.isSideBySide ?? false
          }
        >
          <ToolbarAndPreviewWrapper id="toolbar-preview">
            {!previewConfig?.isOverlay && (
              <PreviewToolbar
                instance={instance.current}
                activeTool={activeTool}
                zoom={zoom}
                showResolvedComments={showResolvedComments}
                filteredDiscussions={filteredDicsussions}
                discussions={discussions}
              />
            )}
            <StyledDiv
              ref={(_current) => {
                ref.current = _current;
              }}
              id={WORKSPACE_ID}
            />
          </ToolbarAndPreviewWrapper>

          {data.sourceType === AttachmentType.Task &&
          !(previewConfig?.isOverlay || previewConfig?.isSideBySide) ? (
            <CommentsSection
              isClose={!isChatSectionVisible}
              isTaskAttachment={data.sourceType === AttachmentType.Task}
            >
              <CommentsHeader>
                {discussions.length > 0 ? (
                  <CommentCount>
                    {discussions.length} <FormattedMessage id="Comments" />
                  </CommentCount>
                ) : (
                  <CommentCount />
                )}
                {resolvedDiscussions.length > 0 ? (
                  <HideResolvedText
                    onClick={() => setShowResolvedComments((value) => !value)}
                  >
                    {showResolvedComments ? (
                      <FormattedMessage id="image_preview.hide.resolved.comments" />
                    ) : (
                      <FormattedMessage id="image_preview.show.resolved.comments" />
                    )}
                  </HideResolvedText>
                ) : null}
              </CommentsHeader>

              {discussionsInAttachment && attachmentId && data ? (
                discussions.length > 0 ? (
                  <div>
                    {discussions?.map((discussion) => (
                      <AttachmentDiscussion
                        activeDiscussion={activeDiscussion}
                        attachmentId={attachmentId}
                        discussion={discussion}
                        instance={instance.current}
                        projectId={data.projectId}
                        setActiveAnnotation={setActiveAnnotation}
                        setActiveDiscussion={setActiveDiscussion}
                        setFloatingFormCoordinates={setFloatingFormCoordinates}
                        showResolvedComments={showResolvedComments}
                      />
                    ))}
                  </div>
                ) : (
                  <StyledEmpty>
                    <EmptyStateContainer $transparent>
                      <EmptyState
                        image={<EmptyStateIcon icon={faCommentsAlt} />}
                        title={
                          <FormattedMessage id="image_preview.comments.empty.title" />
                        }
                        message={
                          <FormattedMessage id="image_preview.comments.empty.message" />
                        }
                      />
                      {resolvedDiscussions.length > 0 ? (
                        <ShowResolvedText
                          onClick={() => setShowResolvedComments(true)}
                        >
                          <FormattedMessage id="image_preview.show.resolved.comments" />
                        </ShowResolvedText>
                      ) : null}
                    </EmptyStateContainer>
                  </StyledEmpty>
                )
              ) : null}
            </CommentsSection>
          ) : (
            !(previewConfig?.isOverlay || previewConfig?.isSideBySide) && (
              <CommentsSection
                isClose={!isChatSectionVisible}
                isTaskAttachment={data.sourceType === AttachmentType.Task}
              >
                <StyledTabs defaultActiveKey="comments">
                  <TabPaneStyled
                    key="comments"
                    tab={<FormattedMessage id="comments" />}
                  >
                    <CommentsHeader>
                      {discussions.length > 0 ? (
                        <CommentCount>
                          {discussions.length}{' '}
                          <FormattedMessage id="Comments" />
                        </CommentCount>
                      ) : (
                        <CommentCount />
                      )}
                      {resolvedDiscussions.length > 0 ? (
                        <HideResolvedText
                          onClick={() =>
                            setShowResolvedComments((value) => !value)
                          }
                        >
                          {showResolvedComments ? (
                            <FormattedMessage id="image_preview.hide.resolved.comments" />
                          ) : (
                            <FormattedMessage id="image_preview.show.resolved.comments" />
                          )}
                        </HideResolvedText>
                      ) : null}
                    </CommentsHeader>
                    {discussionsInAttachment && attachmentId && data ? (
                      discussions.length > 0 ? (
                        <div>
                          {discussions?.map((discussion) => (
                            <AttachmentDiscussion
                              activeDiscussion={activeDiscussion}
                              attachmentId={attachmentId}
                              discussion={discussion}
                              instance={instance.current}
                              projectId={data.projectId}
                              setActiveAnnotation={setActiveAnnotation}
                              setActiveDiscussion={setActiveDiscussion}
                              setFloatingFormCoordinates={
                                setFloatingFormCoordinates
                              }
                              showResolvedComments={showResolvedComments}
                            />
                          ))}
                        </div>
                      ) : (
                        <StyledEmpty>
                          <EmptyStateContainer $transparent>
                            <EmptyState
                              image={<EmptyStateIcon icon={faCommentsAlt} />}
                              title={
                                <FormattedMessage id="image_preview.comments.empty.title" />
                              }
                              message={
                                <FormattedMessage id="image_preview.comments.empty.message" />
                              }
                            />
                            {resolvedDiscussions.length > 0 ? (
                              <ShowResolvedText
                                onClick={() =>
                                  setShowResolvedComments((value) => !value)
                                }
                              >
                                {showResolvedComments ? (
                                  <FormattedMessage id="image_preview.hide.resolved.comments" />
                                ) : (
                                  <FormattedMessage id="image_preview.show.resolved.comments" />
                                )}
                              </ShowResolvedText>
                            ) : null}
                          </EmptyStateContainer>
                        </StyledEmpty>
                      )
                    ) : null}
                  </TabPaneStyled>
                  <TabPane
                    key="activity_log"
                    tab={<FormattedMessage id="image_preview.activity.log" />}
                  >
                    <ListWrapper>
                      {loading ? (
                        <ConversationSkeleton />
                      ) : controlComments && controlComments?.length > 0 ? (
                        <MessagesList messages={controlComments || []} />
                      ) : null}
                    </ListWrapper>
                  </TabPane>
                </StyledTabs>
              </CommentsSection>
            )
          )}
        </CommentAndPreviewWrapper>
      </StyledModal>
    </>
  );
};
